import { Injectable, inject } from '@angular/core';
import { ApiService, GenericResponseResult } from '@brighthub/api';
import { Observable, map } from 'rxjs';
import {
  ActionListPayload,
  ListOpenActionsRequest,
} from './action-api.model';

const API_NAME = 'api-internal';
const ACTION_PATH = '/action';
const OPEN_STATUS_CODES = 'OPEN,BLOCKED';

type Envelope<T> = GenericResponseResult & { payload?: T };

/**
 * Live AIM Actions list. Always sends `assignedToIds` as an Okta uid string.
 */
@Injectable()
export class HttpActionApiService {
  private readonly _api = inject(ApiService);

  listOpenActions(request: ListOpenActionsRequest): Observable<ActionListPayload> {
    const search = request.search?.trim();
    const limit = request.limit ?? 20;
    const offset = request.offset ?? 0;
    return this._api
      .get<Envelope<ActionListPayload>>(
        API_NAME,
        ACTION_PATH,
        { queryParams: this._queryParams(request, search, limit, offset) },
        {
          suppressRequestBodyTelemetry: true,
          telemetryMetadata: {
            hasSearch: Boolean(search),
            hasDueDate: Boolean(request.dueDateStart || request.dueDateEnd),
            hasType: Boolean(request.actionTypeCodes),
            hasPriority: Boolean(request.priorityCodes),
            limit,
            offset,
          },
        },
      )
      .pipe(map((response) => this._payload(response)));
  }

  private _queryParams(
    request: ListOpenActionsRequest,
    search: string | undefined,
    limit: number,
    offset: number,
  ): Record<string, string> {
    const params: Record<string, string> = {
      assignedToIds: request.assignedToId,
      statusCodes: OPEN_STATUS_CODES,
      limit: String(limit),
      offset: String(offset),
    };
    if (search) {
      params['search'] = search;
    }
    if (request.dueDateStart) {
      params['dueDateStart'] = request.dueDateStart;
    }
    if (request.dueDateEnd) {
      params['dueDateEnd'] = request.dueDateEnd;
    }
    if (request.actionTypeCodes) {
      params['actionTypeCodes'] = request.actionTypeCodes;
    }
    if (request.priorityCodes) {
      params['priorityCodes'] = request.priorityCodes;
    }
    return params;
  }

  private _payload(response: Envelope<ActionListPayload>): ActionListPayload {
    if (!response.payload) {
      throw new Error('Actions GET response is missing payload');
    }
    return response.payload;
  }
}
